import React, { useState } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import {
  ArrowLeft,
  Clock,
  ShieldCheck,
  FileCheck2,
  Sparkles,
  ArrowDownRight,
} from "lucide-react";
import Footer from "../components/Footer";
import FloatingSocial from "../components/FloatingSocial";

const FORM_EMBED_URL =
  "https://docs.google.com/forms/d/e/1FAIpQLSfnUgXYBtB2fUL1qmZS_zz1v4He3i-4NcsXi51LcUZ9RIASbg/viewform?embedded=true";
const FORM_DIRECT_URL =
  "https://docs.google.com/forms/d/e/1FAIpQLSfnUgXYBtB2fUL1qmZS_zz1v4He3i-4NcsXi51LcUZ9RIASbg/viewform";

const points = [
  {
    icon: Clock,
    title: "3 minutes",
    body: "Twenty-five short questions across four sections. No prep needed.",
  },
  {
    icon: FileCheck2,
    title: "Personalised report",
    body: "A branded PDF report with your Finance Health Score (0–100) and recommendations.",
  },
  {
    icon: ShieldCheck,
    title: "Strictly confidential",
    body: "Your responses are seen only by CA Roopesh Kumar. Not shared, not sold.",
  },
  {
    icon: Sparkles,
    title: "Free to take",
    body: "No cost. No obligation. Take it once, revisit whenever your business changes.",
  },
];

export default function AssessmentPage() {
  const [loaded, setLoaded] = useState(false);

  return (
    <main
      data-testid="assessment-page"
      className="bg-[#FDFBF7] text-[#3A453F] min-h-screen"
    >
      {/* Slim header */}
      <header className="bg-[#1A2F24] text-[#FDFBF7]">
        <div className="max-w-7xl mx-auto px-6 md:px-12 h-16 flex items-center justify-between">
          <Link
            to="/"
            data-testid="assessment-back-home"
            className="flex items-center gap-2 text-xs tracking-[0.2em] uppercase text-[#C5A059] hover:text-[#FDFBF7] transition-colors"
          >
            <ArrowLeft size={14} /> Back to Home
          </Link>
          <div className="text-[10px] tracking-[0.22em] uppercase text-[#C5A059]">
            Roopesh Kumar &amp; Associates
          </div>
        </div>
      </header>

      {/* Hero */}
      <section className="relative py-20 md:py-28 bg-[#1A2F24] text-[#FDFBF7] grain overflow-hidden">
        <div className="max-w-6xl mx-auto px-6 md:px-12 relative">
          <motion.div
            initial={{ opacity: 0, y: 12 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5 }}
            className="text-[11px] tracking-[0.22em] uppercase text-[#C5A059] mb-5"
          >
            <span className="inline-block w-12 h-px bg-[#C5A059] mr-3 align-middle" />
            Free lead-magnet tool
          </motion.div>

          <motion.h1
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.7 }}
            className="font-serif text-4xl sm:text-5xl md:text-6xl lg:text-7xl leading-[1.02] tracking-tight"
            data-testid="assessment-hero-title"
          >
            Business Finance{" "}
            <em className="italic text-[#C5A059]">Health Assessment</em>
            <span className="text-[#C5A059] align-super text-2xl md:text-3xl">
              ™
            </span>
          </motion.h1>

          <motion.p
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.7, delay: 0.1 }}
            className="mt-8 max-w-2xl text-[#D7D2C6] text-base md:text-lg leading-relaxed"
          >
            A short, honest diagnostic that tells you whether your business is
            ready for a modern <b>Outsourced Finance Department</b> — not just
            another accounting service. We'll evaluate your current finance
            systems, reporting, compliance, and growth readiness — then send
            you a personalised report with our recommendation.
          </motion.p>

          <motion.a
            href="#form"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.7, delay: 0.2 }}
            className="inline-flex items-center gap-2 mt-10 px-8 py-4 bg-[#C5A059] text-[#111814] text-xs tracking-[0.18em] uppercase font-semibold hover:bg-[#D4B47A] transition-colors"
            data-testid="assessment-start-cta"
          >
            Begin the Assessment <ArrowDownRight size={14} />
          </motion.a>
        </div>
      </section>

      {/* Trust points */}
      <section className="py-16 md:py-24 bg-[#FDFBF7]">
        <div className="max-w-6xl mx-auto px-6 md:px-12">
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 md:gap-8">
            {points.map((p, i) => {
              const Icon = p.icon;
              return (
                <motion.div
                  key={p.title}
                  initial={{ opacity: 0, y: 12 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true, margin: "-80px" }}
                  transition={{ duration: 0.5, delay: i * 0.06 }}
                  data-testid={`assessment-point-${i + 1}`}
                  className="border-t border-[#E5E1D8] pt-6"
                >
                  <Icon size={22} strokeWidth={1.4} className="text-[#C5A059]" />
                  <h3 className="font-serif text-2xl text-[#111814] mt-4 leading-tight">
                    {p.title}
                  </h3>
                  <p className="text-[#3A453F] text-[15px] leading-relaxed mt-2">
                    {p.body}
                  </p>
                </motion.div>
              );
            })}
          </div>
        </div>
      </section>

      {/* What you'll be asked */}
      <section className="py-16 md:py-20 bg-[#F5F1E8]/50">
        <div className="max-w-6xl mx-auto px-6 md:px-12">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-10">
            <div className="lg:col-span-5">
              <div className="text-[11px] tracking-[0.22em] uppercase text-[#C5A059] mb-4">
                <span className="inline-block w-12 h-px bg-[#C5A059] mr-3 align-middle" />
                What we'll ask
              </div>
              <h2 className="font-serif text-3xl md:text-4xl lg:text-5xl text-[#111814] leading-tight">
                Four short sections.{" "}
                <em className="italic">One clear picture.</em>
              </h2>
            </div>
            <div className="lg:col-span-7 space-y-6">
              {[
                {
                  n: "A",
                  t: "Business Information",
                  d: "Company, contact, turnover band, headcount, industry.",
                },
                {
                  n: "B",
                  t: "Current Finance Setup",
                  d: "Is your accounting monthly, compliant, and visible? Nine yes/no questions.",
                },
                {
                  n: "C",
                  t: "Pain Points",
                  d: "Where is the current setup slowing you down or costing money?",
                },
                {
                  n: "D",
                  t: "Business Goals",
                  d: "Where you're headed, what you'd change, and whether an OFD fits.",
                },
              ].map((s) => (
                <div
                  key={s.n}
                  data-testid={`assessment-section-${s.n}`}
                  className="flex items-start gap-5 border-b border-[#E5E1D8] pb-6"
                >
                  <div className="font-serif text-3xl text-[#C5A059] w-10 shrink-0">
                    {s.n}
                  </div>
                  <div>
                    <h3 className="font-serif text-xl md:text-2xl text-[#111814]">
                      {s.t}
                    </h3>
                    <p className="text-[15px] text-[#3A453F] mt-1 leading-relaxed">
                      {s.d}
                    </p>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* The form */}
      <section id="form" className="py-16 md:py-24 bg-[#FDFBF7]">
        <div className="max-w-4xl mx-auto px-4 md:px-6">
          <div className="text-center mb-10">
            <div className="text-[11px] tracking-[0.22em] uppercase text-[#C5A059] mb-4">
              Begin
            </div>
            <h2 className="font-serif text-3xl md:text-5xl text-[#111814] leading-tight">
              Take the assessment
            </h2>
            <p className="text-[#68766E] mt-3 text-[15px]">
              Trouble loading?{" "}
              <a
                href={FORM_DIRECT_URL}
                target="_blank"
                rel="noopener noreferrer"
                className="underline underline-offset-4 hover:text-[#1A2F24]"
                data-testid="assessment-form-fallback-link"
              >
                Open the form in a new tab
              </a>
              .
            </p>
          </div>

          <div
            className="relative bg-white border border-[#E5E1D8] shadow-sm overflow-hidden"
            data-testid="assessment-form-frame"
          >
            {!loaded && (
              <div className="absolute inset-0 flex items-center justify-center bg-[#FDFBF7] text-[#68766E] text-xs tracking-[0.2em] uppercase">
                Loading form…
              </div>
            )}
            <iframe
              src={FORM_EMBED_URL}
              title="Business Finance Health Assessment"
              width="100%"
              height="1600"
              frameBorder="0"
              marginHeight="0"
              marginWidth="0"
              onLoad={() => setLoaded(true)}
              className="w-full block"
              data-testid="assessment-iframe"
            >
              Loading…
            </iframe>
          </div>
        </div>
      </section>

      <Footer />
      <FloatingSocial />
    </main>
  );
}
